import React, { useState, useEffect } from 'react';
import { useLocation, Outlet, useNavigate } from 'react-router-dom';
import { setPageTitle } from '../../utils/pageTitle';
import { 
  Book, 
  ChevronLeft, 
  Home,
  Settings,
  Menu,
  X,
  Star,
  Shield
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { employeeNamesService } from '../../services/employeeNames';
import { UserInfoSection } from '../layout/UserInfoSection';
import { useDarkMode } from '../../context/DarkModeContext';
import '../../styles/glossary-override.css';

/**
 * Layout component for the Glossary app
 * Provides navigation sidebar and applies glossary theme
 */
export const GlossaryLayout: React.FC = () => {
  const [displayName, setDisplayName] = useState<string>('');
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userEmail } = useAuth();
  const { isAdmin } = useIsAdmin();
  const { isDarkMode } = useDarkMode();
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth < 768);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const loadDisplayName = async () => {
      if (userEmail) {
        const name = await employeeNamesService.getNameFromEmail(userEmail);
        if (name) {
          setDisplayName(name);
        }
      }
    };
    loadDisplayName();
  }, [userEmail]);

  // Set page title based on current route
  useEffect(() => {
    const path = location.pathname;
    let title = 'Glossary';
    
    if (path === '/glossary' || path === '/glossary/dashboard') {
      title = 'Glossary';
    } else if (path.startsWith('/glossary/favorites')) {
      title = 'Favorites | Glossary';
    } else if (path.startsWith('/glossary/settings')) {
      title = 'Settings | Glossary';
    }
    
    setPageTitle(title);
  }, [location.pathname]);

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  const handleNavigate = (to: string) => {
    navigate(to);
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="h-screen bg-gray-50 dark:bg-gray-900 flex overflow-hidden glossary-app">
      {/* Mobile Menu Button */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-white dark:bg-gray-800 z-40 border-b dark:border-gray-700 px-4 flex items-center">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
        >
          {isMobileMenuOpen ? (
            <X className="h-6 w-6 text-gray-600" />
          ) : (
            <Menu className="h-6 w-6 text-gray-600" />
          )}
        </button>
      </div>

      {/* Mobile Menu Backdrop */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          bg-white dark:bg-gray-800 shadow-sm fixed md:relative h-full z-40
          transform transition-transform duration-300 ease-in-out flex flex-col
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0 hidden md:flex'}
          ${isCollapsed && !isMobileMenuOpen ? 'w-16 md:w-16' : 'w-64 md:w-64'}
        `}
      >
        {/* Collapse Toggle - Hidden on Mobile */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute -right-3 top-6 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-full p-1 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 hidden md:block"
        >
          <ChevronLeft className={`h-4 w-4 text-gray-600 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`} />
        </button>

        <div className="border-b flex justify-center items-center h-46">
          {isCollapsed && !isMobileMenuOpen ? (
            <div className="h-12 w-12 text-indigo-500 mb-1 mt-1">
              <Book className="h-10 w-10" />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="bg-indigo-500 p-3 rounded-lg mb-2">
                <Book className="h-8 w-8 text-white" />
              </div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">Glossary</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">Learn JLS Terms</p>
            </div>
          )}
        </div>
        
        <nav className="flex-1 mt-4 overflow-y-auto flex flex-col">
          {/* Main Navigation */}
          <div
            onClick={() => handleNavigate('/glossary/dashboard')}
            className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
              isActive('/glossary/dashboard') ? 'bg-indigo-50 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-300' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <Book className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
            {(!isCollapsed || isMobileMenuOpen) && 'Glossary List'}
          </div>

          <div
            onClick={() => handleNavigate('/glossary/favorites')}
            className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
              isActive('/glossary/favorites') ? 'bg-indigo-50 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-300' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <Star className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
            {(!isCollapsed || isMobileMenuOpen) && 'Favorites'}
          </div>

          {/* Divider */}
          <div className="mx-4 my-4 border-t border-gray-200 dark:border-gray-700" />

          {/* Utility Navigation */}
          <div
            onClick={() => handleNavigate('/glossary/settings')}
            className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
              isActive('/glossary/settings') ? 'bg-indigo-50 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-300' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <Settings className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
            {(!isCollapsed || isMobileMenuOpen) && 'Settings'}
          </div>

          {/* Admin Link - Only shown to admin users */}
          {isAdmin && (
            <div
              onClick={() => handleNavigate('/glossary/admin')}
              className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
                isActive('/glossary/admin') ? 'bg-indigo-50 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-300' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              <Shield className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
              {(!isCollapsed || isMobileMenuOpen) && 'Admin'}
            </div>
          )}

          {/* Apps Hub Link */}
          <div
            onClick={() => handleNavigate('/dashboard')}
            className="flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
          >
            <Home className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
            {(!isCollapsed || isMobileMenuOpen) && 'Apps Hub'}
          </div>
        </nav>

        <UserInfoSection 
          displayName={displayName} 
          isCollapsed={isCollapsed && !isMobileMenuOpen} 
        />
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 overflow-auto pt-16 md:pt-0 relative">
        <Outlet />
      </main>
    </div>
  );
};